<script setup>
import { ref, computed } from 'vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconExternalLink from '@/components/icon/IconExternalLink.vue'

/**
 * Preview of the public embed player for the captioned stream. Captions show
 * up here once the CDN routes the stream through the captions origin.
 *
 * "Open player" opens the same public player URL in a new tab (`embedUrl` is
 * core's `playback.embed`, or `VUE_APP_PLAYER_APP_BASE_URL/<streamKey>` when
 * core omits it; see CaptionService.normalizeFlow), so what the publisher
 * checks there is exactly what the preview shows.
 *
 * `reload()` is exposed so the parent can refresh the player once a language
 * first turns live: the master playlist only lists subtitle tracks at load
 * time, so a player that loaded before captions started will not show them
 * until it reloads.
 */
const props = defineProps({
	embedUrl: { type: String, default: '' },
	streamName: { type: String, default: '' },
	// Line under the player; SyncWords flows explain their subtitle tracks instead.
	note: { type: String, default: 'Captions appear in the player once your stream is routed through the captions origin.' },
})

const emit = defineEmits(['reloaded'])

const embedNonce = ref(0)
const reloading = ref(false)
// Exposed on the root as data-reload-count so the parent's "reload once"
// behaviour is observable (and testable).
const reloadCount = ref(0)

const hasEmbed = computed(() => !!props.embedUrl)

// The preview exists to check captions, so they start on (`cc=on`) but the CC
// toggle stays, so the publisher can switch them off or change language.
// "Open player" stays the plain public URL so the publisher can also see what a
// viewer gets by default.
const embedSrc = computed(() => {
	if (!props.embedUrl) return ''
	const sep = props.embedUrl.includes('?') ? '&' : '?'
	return `${props.embedUrl}${sep}cc=on&r=${embedNonce.value}`
})

function reload() {
	reloading.value = true
	reloadCount.value += 1
	embedNonce.value += 1
	emit('reloaded')
	setTimeout(() => { reloading.value = false }, 600)
}

defineExpose({ reload })
</script>

<template>
	<section class="flex flex-col gap-3" aria-label="Caption preview">
		<div class="flex flex-wrap items-center justify-between gap-2">
			<span class="text-tiny font-medium text-surface-text-1">Player preview</span>
			<div class="flex flex-wrap items-center justify-end gap-2">
				<slot name="actions" />
				<FormButton type="secondary" size="sm" :is-loading="reloading" :disabled="!hasEmbed" @click="reload">Reload player</FormButton>
				<FormButton
					v-if="hasEmbed"
					type="secondary"
					size="sm"
					class="gap-1.5"
					:href="embedUrl"
					target="_blank"
					rel="noopener"
					title="Open the public player in a new tab"
					data-testid="open-player"
				>
					Open player
					<icon-base width="12" height="12" class="text-surface-8"><icon-external-link /></icon-base>
				</FormButton>
			</div>
		</div>

		<div
			class="captions-player relative w-full overflow-hidden rounded-lg bg-black"
			style="aspect-ratio: 16 / 9"
			:data-reload-count="reloadCount"
		>
			<iframe
				v-if="hasEmbed"
				:key="embedNonce"
				:src="embedSrc"
				class="absolute inset-0 h-full w-full border-0"
				allow="autoplay; encrypted-media"
				allowfullscreen
				:title="streamName ? `Player for ${streamName}` : 'Public embed player'"
			/>
			<div v-else class="absolute inset-0 flex items-center justify-center px-6 text-center text-tiny text-surface-8">
				No public player URL is available for this stream.
			</div>
		</div>

		<p class="m-0 text-xs text-surface-7">{{ note }}</p>
	</section>
</template>
